import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BaseService } from '../../services/base.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Ticket, TicketForm } from './tab-support-ticket.interface';
import { StackedSidebarService } from '../../services/stacked-sidebar.service';
import { SidebarType } from '../../components/layout/stacked-sidebar/stacked-sidebar.interface';
import { MatDialog } from '@angular/material/dialog';
import { TicketService } from '../../services/ticket.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { DatePipe } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { FileUploadNewComponent } from '../../modals/file-upload-new/file-upload-new.component';
import { ModalService } from '../../services/modal.service';
import { ATTACHMENTS_TYPES, UPLOAD_FILE_TYPES } from '../../enum/constants';
import { NotificationService } from '../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-tab-support-ticket',
  templateUrl: './tab-support-ticket.component.html',
  styleUrls: ['./tab-support-ticket.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabSupportTicketComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<null>();

  public params = {
    page: 1,
    take: 25,
    search: '',
  };

  public totalItems = 0;
  currentIndex: any = 0;
  currentTake: any = 0;
  public ticketForm!: FormGroup<TicketForm>;
  public ticketList: any[] = [];
  public ticketFiles: any[] = [];
  public isLoader: boolean = false;
  public searchQuery: string = '';
  private ticketSubscription: Subscription;

  constructor(
    public readonly baseService: BaseService,
    public readonly ticketService: TicketService,
    public stackedSidebarService: StackedSidebarService,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private readonly modalService: ModalService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
  ) {
    this.ticketSubscription = Subscription.EMPTY;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.initForm();
    this.initTickets(this.params);
  }

  public initForm() {
    this.ticketForm = new FormGroup<TicketForm>({
      name: new FormControl('', [Validators.required, Validators.pattern(/\S+/)]),
      text: new FormControl('', [Validators.required, Validators.pattern(/\S+/)]),
    });
  }

  public initTickets(params: any, search: string = '') {
    this.ticketList.splice(0, this.ticketList.length);
    const payload = {
      page: params.page,
      take: params.take,
      search: search,
    };

    this.isLoader = true;
    if (this.ticketSubscription) {
      this.ticketSubscription.unsubscribe();
    }
    this.ticketSubscription = this.ticketService
      .getAllTickets(payload)
      .subscribe((res: { items: Ticket[]; total: number }) => {
        this.ticketList.push(...res.items);
        this.totalItems = res.total;
        this.isLoader = false;
      });
  }

  public onCreateTicket() {
    if (this.ticketForm.valid) {
      const ticket = this.ticketForm.getRawValue();

      const payload = {
        name: ticket.name,
        text: ticket.text,
        status: 'active',
        attachment: this.ticketFiles,
      };
      this.ticketService
        .addTicket(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.ticketForm.reset();
            this.ticketFiles.splice(0, this.ticketFiles.length);
            this.initTickets(this.params);
          },
        });
    }
  }

  onOpenCard(ticket: Ticket) {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.Ticket,
          maxWidth: '50vw',
        },
        {
          data: {
            ticket: ticket,
          },
        },
      )
      .subscribe();
  }

  openFileUpload() {
    const maxUploadFiles = UPLOAD_FILE_TYPES['TICKET'].max_upload_files;
    const allowedMediaTypes = UPLOAD_FILE_TYPES['TICKET'].allowed_media_types;

    if (maxUploadFiles && this.ticketFiles.length >= maxUploadFiles) {
      this.notificationService.showWarning(
        this.translate.instant('notifications_texts.warn.warn'),
        this.translate.instant('notifications_texts.warn.no_more_upload_files', { limit: maxUploadFiles }),
      );
      return;
    }

    this.modalService
      .openModal({
        translate: true,
        component: FileUploadNewComponent,
        title: 'upload_file',
        height: 'auto',
        data: {
          length: this.ticketFiles.length,
          max_upload_files: maxUploadFiles,
          allowed_media_types: allowedMediaTypes,
        },
      })
      .subscribe((res: any[]) => {
        if (res) {
          this.ticketFiles.push(
            ...res.map((v) => ({
              url: v.link,
              mimeType: v.mimeType,
              type: ATTACHMENTS_TYPES.IMAGE,
              name: v.name,
              size: v.size,
            })),
          );
        }
      });
  }

  deletePhoto(index: number) {
    this.ticketFiles.splice(index, 1);
  }

  formatDate(date: string): string {
    return this.datePipe.transform(date, 'dd.MM.yyyy HH:mm') ?? '';
  }

  onSearchChange(search: string): void {
    const trimmedSearch = search.trim();
    this.searchQuery = trimmedSearch;
    this.currentIndex = 0;
    this.initTickets({ page: this.currentIndex + 1, take: this.currentTake }, trimmedSearch);
  }

  onPaginationChange(paginationDetails: PageEvent) {
    const { pageIndex, pageSize } = paginationDetails;
    this.currentIndex = pageIndex;
    this.currentTake = pageSize;
    this.initTickets({ page: pageIndex + 1, take: pageSize });
  }
}
